import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";

export interface CampaignPlanRunReadiness {
  brand_id: string;
  campaign_id: string;
  plan_id: string;
  revision: number;
  can_run: boolean;
  blockers: Array<{ code: string; message: string }>;
  campaign_status: string;
  action_status: "planned" | "archived";
  definition_version: number;
}

export interface CampaignPlanRun {
  id: string;
  brand_id: string;
  campaign_id: string;
  invocation_id: string;
  plan_id: string;
  plan_revision: number;
  definition_version: number;
  status: "running" | "retrying" | "completed" | "failed" | "skipped";
  started_at: string | null;
  completed_at: string | null;
  error: string | null;
  summary: string | null;
  work_products?: Array<Record<string, unknown>>;
}

export interface CampaignPlanRunAdmission {
  run_id: string;
  brand_id: string;
  campaign_id: string;
  invocation_id: string;
  plan_id: string;
  plan_revision: number;
  status: CampaignPlanRun["status"];
}

export interface CampaignPlanRunIntent {
  invocation_id: string;
  expected_revision: number;
}

function collectionPath(campaignId: string, planId: string) {
  return `/api/campaigns/${encodeURIComponent(campaignId)}/action-plans/${encodeURIComponent(planId)}`;
}

export function campaignPlanRunIntentKey(brandId: string, campaignId: string, planId: string) {
  return `campaign-plan-run:${encodeURIComponent(brandId)}:${encodeURIComponent(campaignId)}:${encodeURIComponent(planId)}`;
}

export function readCampaignPlanRunIntent(brandId?: string, campaignId?: string, planId?: string): CampaignPlanRunIntent | null {
  if (!brandId || !campaignId || !planId || typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(campaignPlanRunIntentKey(brandId, campaignId, planId));
    if (!raw) return null;
    const value = JSON.parse(raw) as Partial<CampaignPlanRunIntent>;
    if (typeof value.invocation_id !== "string" || !Number.isInteger(value.expected_revision)) return null;
    return { invocation_id: value.invocation_id, expected_revision: value.expected_revision! };
  } catch {
    return null;
  }
}

export function writeCampaignPlanRunIntent(brandId: string, campaignId: string, planId: string, intent: CampaignPlanRunIntent) {
  if (typeof window !== "undefined") {
    window.localStorage.setItem(campaignPlanRunIntentKey(brandId, campaignId, planId), JSON.stringify(intent));
  }
}

export function clearCampaignPlanRunIntent(brandId: string, campaignId: string, planId: string) {
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(campaignPlanRunIntentKey(brandId, campaignId, planId));
  }
}

export function useCampaignPlanRunReadiness(brandId?: string, campaignId?: string, planId?: string) {
  return useQuery({
    queryKey: ["campaign-plan-run-readiness", brandId, campaignId, planId],
    enabled: !!brandId && !!campaignId && !!planId,
    retry: false,
    queryFn: () => api.get<{ data: CampaignPlanRunReadiness }>(
      `${collectionPath(campaignId!, planId!)}/readiness?brand_id=${encodeURIComponent(brandId!)}`,
    ),
  });
}

export function useCampaignPlanRuns(brandId?: string, campaignId?: string, planId?: string) {
  return useQuery({
    queryKey: ["campaign-plan-runs", brandId, campaignId, planId],
    enabled: !!brandId && !!campaignId && !!planId,
    retry: false,
    queryFn: () => api.get<{ data: CampaignPlanRun[] }>(
      `${collectionPath(campaignId!, planId!)}/runs?brand_id=${encodeURIComponent(brandId!)}`,
    ),
  });
}

export function useCampaignPlanRunRecovery(
  brandId?: string,
  campaignId?: string,
  planId?: string,
  invocationId?: string,
) {
  return useQuery({
    queryKey: ["campaign-plan-run-recovery", brandId, campaignId, planId, invocationId],
    enabled: !!brandId && !!campaignId && !!planId && !!invocationId,
    retry: false,
    queryFn: async () => {
      try {
        const response = await api.get<{ data: CampaignPlanRun }>(
          `${collectionPath(campaignId!, planId!)}/runs/${encodeURIComponent(invocationId!)}?brand_id=${encodeURIComponent(brandId!)}`,
        );
        return response.data;
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
  });
}

export function useAdmitCampaignPlanRun(brandId?: string, campaignId?: string, planId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (intent: CampaignPlanRunIntent) => {
      if (!brandId || !campaignId || !planId) throw new Error("Missing campaign plan context");
      return api.post<{ data: CampaignPlanRunAdmission }>(
        `${collectionPath(campaignId, planId)}/runs`,
        { brand_id: brandId, expected_revision: intent.expected_revision, invocation_id: intent.invocation_id },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["campaign-plan-runs", brandId, campaignId, planId] });
    },
  });
}
